import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion, useSpring } from 'framer-motion';
import { useSFX } from 'js/hooks/useSFX';
import { isDesktop } from 'js/utils/deviceDetection';

// actions
import { updateRecentlyViewedCards } from 'js/store/user/User.actions';
import { releaseActivityCardFocus } from 'js/store/app/App.actions';

// components
import ActivityCardFront from 'js/components/activity-card/ActivityCard.front';
import ActivityCardBack from 'js/components/activity-card/ActivityCard.back';

// utils
import { triggerHapticVibration } from 'js/utils/haptics';
import { trackEvent } from '../../utils/tracking';

// import icons
import IconChevronRight from 'js/components/icon/Icon.chevron-right';
import IconChevronLeft from 'js/components/icon/Icon.chevron-left';

const SPRING = {
  type: 'spring',
  stiffness: 300,
  damping: 40,
};

const LONG_PRESS_MS = 200;
const MOVE_THRESHOLD = 10;

const ActivityCard = ({ allowFlip = true, onFlip = false, data, forceBackView = false }) => {
  const dispatch = useDispatch();
  const locale = useSelector((state) => state.app.locale);

  const paginationCount = locale == 'en' ? data.back.paginationCount : data.back.localisation.localizedPaginationCount;

  const [isFlipped, setIsFlipped] = useState(forceBackView);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const { playSFX } = useSFX();

  const swiperRef = useRef(null);

  // gesture tracking
  const touchStartTime = useRef(0);
  const startPos = useRef({ x: 0, y: 0 });
  const hasMoved = useRef(false);

  // tilt springs
  const rotateX = useSpring(0, SPRING);
  const rotateY = useSpring(0, SPRING);

  const updatePagination = useCallback(() => {
    const swiper = swiperRef.current?.swiper;
    if (swiper) {
      playSFX('swiping_through_library_cards');
      setCurrentSlideIndex(swiper.activeIndex);
    }
  }, []);

  const releaseActivityCardFocusState = useSelector((state) => state.app.releaseActivityCardFocus);

  useEffect(() => {
    if (releaseActivityCardFocusState && isFlipped) {
      triggerFlipAction();
      dispatch(releaseActivityCardFocus(false));
    }
  }, [releaseActivityCardFocusState]);

  useEffect(() => {
    const swiper = swiperRef.current?.swiper;
    if (!swiper) return;

    if (isFlipped) {
      trackEvent('game_content_view', { content_id: data.id, content_title: data.title });

      dispatch(updateRecentlyViewedCards(data.id));
      swiper.slideTo(0, 0);
      swiper.on('slideChange', updatePagination);
    } else {
      swiper.off('slideChange', updatePagination);
      setCurrentSlideIndex(0);
    }

    return () => {
      swiper.off('slideChange', updatePagination);
    };
  }, [isFlipped]);

  const onPointerDown = (e) => {
    touchStartTime.current = Date.now();
    hasMoved.current = false;

    startPos.current = {
      x: e.clientX,
      y: e.clientY,
    };
  };

  const onPointerMove = (e) => {
    const deltaX = Math.abs(e.clientX - startPos.current.x);
    const deltaY = Math.abs(e.clientY - startPos.current.y);

    if (deltaX > MOVE_THRESHOLD || deltaY > MOVE_THRESHOLD) {
      hasMoved.current = true;
    }
  };

  const onPointerUp = (e) => {
    if (!allowFlip || e.target.classList.value.includes('swiper-button')) return;

    // Prevent flipping when interacting with child controls
    if (
      isFlipped ||
      e.target.id === 'flip-icon-svg' ||
      e.target.parentNode.classList.value.includes('card__icon--flip') ||
      e.target.classList.value.includes('card__image') ||
      e.target.parentNode.classList.value.includes('card__image') ||
      e.target.classList.value.includes('card__details') ||
      e.target.parentNode.classList.value.includes('card__details')
    ) {
      const elapsed = Date.now() - touchStartTime.current;
      elapsed < LONG_PRESS_MS && !hasMoved.current && triggerFlipAction();
    }
  };

  const triggerFlipAction = () => {
    playSFX('card_rotate_3');
    triggerHapticVibration();
    setIsFlipped((prev) => !prev);
    // console.log('flip', onFlip);

    onFlip && onFlip();
  };

  return (
    <motion.div
      className={`card__flip ${!allowFlip ? 'card--reduced-view' : ''}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      transition={SPRING}
      data-card-id={`card-${data.id}`}
    >
      <motion.div className="card__scale-container" style={{ rotateX, rotateY }} transition={SPRING}>
        <div className="card__flip-container">
          <motion.div
            className="card__face-container"
            animate={{ rotateY: isFlipped ? -180 : 0 }}
            transition={SPRING}
            style={{ zIndex: isFlipped ? 0 : 1 }}
          >
            <ActivityCardFront data={data} fullView={allowFlip} />
          </motion.div>

          <motion.div
            className="card__face-container card__face-container--back"
            initial={{ rotateY: 180 }}
            animate={{ rotateY: isFlipped ? 0 : 180 }}
            transition={SPRING}
            style={{ zIndex: isFlipped ? 1 : 0 }}
          >
            <ActivityCardBack data={data} swiperRef={swiperRef} />
          </motion.div>
        </div>
      </motion.div>

      {allowFlip && (
        <>
          {isDesktop() !== 'smartphone' && isDesktop() !== 'tablet' && (
            <>
              <div
                className={`swiper-button-prev__card-back swiper-button-prev swiper-button-prev__card-back-${data.id}`}
                style={{ display: isFlipped ? 'block' : 'none', left: '-40px' }}
              >
                <IconChevronLeft />
              </div>
              <div
                className={`swiper-button-next__card-back swiper-button-next swiper-button-next__card-back-${data.id}`}
                style={{ display: isFlipped ? 'block' : 'none', right: '-60px' }}
              >
                <IconChevronRight />
              </div>
            </>
          )}
          <div className={`card__pagination-indicator${isFlipped ? ' card__pagination-indicator--active' : ''}`}>
            {Array.from({ length: paginationCount }).map((_, index) => (
              <button
                key={index}
                className={`swiper-button-pagination card__pagination-dot${
                  isFlipped && index === currentSlideIndex ? ' card__pagination-dot--active' : ''
                }`}
                onClick={() => {
                  if (isFlipped && swiperRef.current) {
                    playSFX('button_tap');
                    swiperRef.current.swiper.slideTo(index);
                  }
                }}
                aria-label={`Go to page ${index + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </motion.div>
  );
};

export default ActivityCard;
